import React, { useEffect, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import useTerritory from '../Context/hook/useTerritory'
import InputMask from 'react-input-mask'
import Select from 'react-select'
import { colourStyles } from '../../../Utils/styled-components/react-select.style'
import { PERSONA_CEDULA } from '../../../Utils/url.api'
import { getRequest } from '../../../Services/axios.service'
import { normalizePhone } from '../../../Utils/normalize'
import { Box } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import { AiOutlineDelete, AiOutlineEdit } from 'react-icons/ai'
import { CONTRIBUTORS } from '../Utils/contributors-url'

export const GeneratorServiceProvider = ({ documentTypes, phoneTypes, onAdd, onEdit, onClose, patchValue }) => {
    const { control, formState: { errors }, watch, register, setValue, getValues, handleSubmit, reset } = useForm({
        defaultValues: {
            id: 0
        },
        shouldUnregister: true,
        mode: 'onBlur'
    })
    
    const [formMode, setFormMode] = useState("NEW")
    const { fetchDistricts, fetchMunicipalities, fetchProvinces, loadingSet, territories } = useTerritory()

    const [documentNumber, setDocumentNumber] = useState(false)
    const [inputConfig, setInputConfig] = useState(
        { documentError: '' }
    )

    const watchProvince = useWatch({control, name: 'provincia'})
    const watchMunicipality = useWatch({control, name: 'municipio'})
    const watchDocument = useWatch({control, name: 'numeroDocumento'})
    const watchDocumentId = useWatch({control, name: 'documentTypeId'})

    useEffect(() => {
        if(patchValue.state) reset(patchValue.state)
        setFormMode((cur) => patchValue.index !== null ? "EDIT" : "NEW")
        fetchProvinces()  
    }, [])

    useEffect(() => {
        cascadeDropdown()        
    }, [watchMunicipality, watchProvince])

    const cascadeDropdown = async () => {
        if(watchProvince) await fetchMunicipalities(watchProvince)
        if(watchMunicipality && watchProvince) await fetchDistricts(watchMunicipality, watchProvince)
    } 

    const validateDocumentNumber = async (value) => {
        const document = value.replace(/-/g, "")
        const documentTypeId = getValues("documentTypeId")
        const identificator = documentTypes.find(d => d.id === documentTypeId)?.identificador
        if (identificator === '1' && document.length >= 11) {
            setDocumentNumber(cur => true)
            const response = await getRequest(PERSONA_CEDULA(document))
            setDocumentNumber(cur => false)
            const { statusCode, data } = response.data
            if (statusCode === 200 && data) {
                setValue("nombre", data.fullName.toLowerCase().replace(/(^\w{1})|(\s+\w{1})/g, letter => letter.toUpperCase()), { shouldValidate: true });
                return true
            }
            else if (statusCode === 200 && !data) {
                setInputConfig(curr => ({ ...curr, documentError: "Cédula no valida" }))
                return false
            }
            else {
                setInputConfig(curr => ({ ...curr, documentError: 'No se pudo validar la cédula' }))
                return false
            }
        }
        if (identificator === '2') {
            setDocumentNumber(cur => true)
            const { data } = await getRequest(CONTRIBUTORS(document), {baseURL: process.env.REACT_APP_OGTIC_CONTRIBUTORS})
            setDocumentNumber(cur => false)
            if (data.valid) {
                setValue("nombre", data.data.name, { shouldValidate: true });
                return true
            }
            else {
                setInputConfig(curr => ({ ...curr, documentError: "RNC no valida" }))
                return false
            }
        }
        return true
    }

    const submitForm = (formValue, event) => {
        event.stopPropagation()
        event.preventDefault()
        const cleanedValues = {
            ...formValue,
            telefono: normalizePhone(formValue?.telefono).toString(),
            numeroDocumento: formValue?.numeroDocumento.replace(/-/g, "")
        }
        formMode === 'EDIT' ? onEdit(cleanedValues, patchValue.index) : onAdd(cleanedValues)
        onClose(false)
    }

    return (
        <div className="">
            <form className="custom-form grid grid-cols-1 tab-port:grid-cols-4 small-desktop:grid-cols-[repeat(8,1fr)] 
                gap-x-8 px-8"
                // onSubmit={handleSubmit(submitForm)}
                >
                <input type="hidden" {...register('id',
                    { required: { message: 'El id es requerido', value: true } }
                )} />
                <div className="custom-form__control col-span-full tab-port:col-span-2">
                    <label className='custom-form__label'>
                        Tipo de Identificación
                        <span className="text-danger"> *</span>
                    </label>
                    <Controller
                        control={control}
                        name="documentTypeId"
                        rules={{ required: { value: true, message: 'Este campo es requerido' } }}
                        render={({
                            field: { onChange, name, value, ref, onBlur },
                        }) => (
                            <Select
                                ref={ref}
                                onBlur={onBlur}
                                styles={colourStyles}
                                noOptionsMessage={() => "Sin datos"}
                                placeholder="Seleccione un tipo"
                                getOptionLabel={(option) => option?.description}
                                getOptionValue={(option) => option?.id}
                                options={documentTypes}
                                onChange={(newValue) => {
                                    setValue('nombre', null) 
                                    setValue('numeroDocumento', null) 
                                    onChange(newValue?.id)
                                }}
                                value={documentTypes?.find(c => c?.id === value)}
                            />
                        )}>
                    </Controller>
                    <span>
                        {errors.documentTypeId?.type === 'required' &&
                            <p className='text-danger' role="alert">{errors.documentTypeId?.message}</p>}
                    </span>
                </div>
                <div className="custom-form__control col-span-full tab-port:col-span-2">
                    <label className='custom-form__label' htmlFor='numeroDocumento'>
                        Identificación
                        <span className="text-danger"> *</span>
                    </label>
                    <div className="flex gap-x-2 items-center justify-between">
                        <Controller
                            control={control}
                            name="numeroDocumento"
                            rules={{
                                required: { message: 'Este campo es requerido', value: true },
                                validate: {
                                    valid: async (val) => await validateDocumentNumber(val)
                                }
                            }}
                            render={({
                                field: { onChange, name, value, ref, onBlur },
                            }) => (
                                <InputMask
                                    id="numeroDocumento"
                                    className='custom-form__input w-full'
                                    onChange={(event) => {
                                        if(watchDocumentId !== 3) {
                                            setValue('nombre', null) 
                                        }
                                        onChange(event.target.value)
                                    }}
                                    onBlur={onBlur}
                                    inputRef={ref}
                                    mask={watch("documentTypeId") === 1 ? '999-9999999-9' : ''}
                                    maskChar={null}
                                    value={value ?? ""}
                                    disabled={!watch("documentTypeId")}
                                />
                            )}>
                        </Controller>
                        {documentNumber && <div className="spinner-loading"></div>}
                    </div>
                    <span>
                        {errors.numeroDocumento?.type === 'required' &&
                            <p className='text-danger' role="alert">{errors.numeroDocumento?.message}</p>}
                        {errors.numeroDocumento?.type === 'valid' &&
                            <p className='text-danger' role="alert">{inputConfig?.documentError}</p>}
                    </span>
                </div>
                <div className="custom-form__control col-span-full tab-port:col-span-4">
                    <label className='custom-form__label' htmlFor="nombre">
                        Nombre Completo
                        <span className="text-danger"> *</span>
                    </label>
                    <input className='custom-form__input' id="nombre" type="text"
                        disabled={watch("documentTypeId") !== 3}
                        {...register('nombre',
                            { required: { message: 'Este campo es requerido', value: true } }
                        )} />
                    <span>
                        {errors.nombre?.type === 'required' &&
                            <p className='text-danger' role="alert">{errors.nombre?.message}</p>}
                    </span>
                </div>
                <div className="custom-form__control col-span-full tab-port:col-span-2">
                    <label className='custom-form__label'>
                        Tipo Teléfono
                        <span className="text-danger"> *</span>
                    </label>
                    <Controller
                        control={control}
                        name="phoneTypeId"
                        rules={{
                            required: { value: true, message: 'Este campo es requerido' }
                        }}
                        render={({
                            field: { onChange, name, value, ref, onBlur },
                        }) => (
                            <Select
                                ref={ref}
                                onBlur={onBlur}
                                styles={colourStyles}
                                noOptionsMessage={() => "Sin datos"}
                                placeholder="Seleccione un tipo"
                                getOptionLabel={(option) => option?.description}
                                getOptionValue={(option) => option?.id}
                                options={phoneTypes}
                                onChange={(newValue) => onChange(newValue?.id)}
                                value={phoneTypes?.find(c => c?.id === value) ?? null}
                            />
                        )}>
                    </Controller>
                    <span>
                        {errors.phoneTypeId?.type === 'required' &&
                            <p className='text-danger' role="alert">{errors.phoneTypeId?.message}</p>}
                    </span>
                </div>
                <div className="custom-form__control col-span-full tab-port:col-span-2">
                    <label className='custom-form__label' htmlFor='telefono'>
                        Teléfono
                        <span className="text-danger"> *</span>
                    </label>
                    <Controller
                        control={control}
                        name="telefono"
                        rules={{
                            required: { message: 'Este campo es requerido', value: true },
                        }}
                        render={({
                            field: { onChange, name, value, ref, onBlur },
                        }) => (
                            <InputMask
                                id="telefono"
                                className='custom-form__input'
                                onChange={(event) => onChange(event.target.value)}
                                onBlur={onBlur}
                                inputRef={ref}
                                mask={'(999)-999-9999'}
                                maskChar={null}
                                value={value ?? ""}
                                disabled={!watch("phoneTypeId")}
                            />
                        )}>
                    </Controller>
                    <span>
                        {errors.telefono?.type === 'required' &&
                            <p className='text-danger' role="alert">{errors.telefono?.message}</p>}
                    </span>
                </div>
                <div className="custom-form__control col-span-full tab-port:col-span-4">
                    <label className='custom-form__label' htmlFor='correo'>
                        Correo Electrónico
                        <span className="text-danger"> *</span>
                    </label>
                    <input className='custom-form__input' id="correo" type="email"
                        {...register('correo',
                            {
                                required: { message: 'Este campo es requerido', value: true },
                                pattern: {
                                    value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                                    message: 'Correo invalido',
                                }
                            })} />
                    <span>
                        {errors.correo?.type === 'required' &&
                            <p className='text-danger' role="alert">{errors.correo?.message}</p>}
                        {errors.correo?.type === 'pattern' && <p className='text-danger' role="alert">{errors.correo?.message}</p>}
                    </span>
                </div>
                <div className="custom-form__control col-span-full tab-port:col-span-2">
                    <label className='custom-form__label' htmlFor='correo'>
                        Nacionalidad
                    </label>
                    <input className='custom-form__input' id="correo" type="text"
                        {...register('nacionalidad')} />
                </div>
                <div className="custom-form__control col-span-full tab-port:col-span-2">
                    <label className='custom-form__label' htmlFor='numeroAutorizacionAmbiental'>
                        Número de Autorización de Manejo de Residuos
                    </label>
                    <input className='custom-form__input' id="numeroAutorizacionAmbiental" type="text"
                        {...register('numeroAutorizacionAmbiental')} />
                </div>
                <div className="custom-form__control col-span-full tab-port:col-span-2">
                    <label className='custom-form__label' htmlFor='vencimientoAutorizacion'>
                        Fecha de Vencimiento Autorización
                    </label>
                    <input className='custom-form__input' id="vencimientoAutorizacion" type="date"
                        {...register('vencimientoAutorizacion')} />
                </div>

                <div className="custom-form__control col-span-full">
                    <label className='custom-form__label' htmlFor="direccion">
                        Domicilio
                        <span className="text-danger"> *</span>
                    </label>
                    <textarea className="custom-form__input" id="direccion" cols="30" rows="3"
                        {...register('direccion',
                            {
                                required: { message: 'Este campo es requerido', value: true }
                            })} />
                    <span>
                        {errors.direccion?.type === 'required' &&
                            <p className='text-danger' role="alert">{errors.direccion?.message}</p>}
                    </span>
                </div>

                <div className="custom-form__control col-span-full tab-port:col-span-2">
                    <label className='custom-form__label'>
                        Provincia
                        <span className="text-danger"> *</span>
                    </label>
                    <Controller
                        control={control}
                        name="provincia"
                        rules={{ required: { value: true, message: 'Este campo es requerido' } }}
                        render={({
                            field: { onChange, name, value, ref, onBlur },
                        }) => (
                            <Select
                                ref={ref}
                                onBlur={onBlur}
                                styles={colourStyles}
                                menuPlacement="top"
                                noOptionsMessage={() => "Sin datos"}
                                placeholder="Seleccione una provincia"
                                isLoading={loadingSet.provinces}
                                getOptionLabel={(option) => option?.name}
                                getOptionValue={(option) => option?.code}
                                options={territories.provinces}
                                onChange={(newValue) => {
                                    onChange(newValue?.code)
                                    setValue("municipio", null)
                                    setValue("ciudad", null)
                                }}
                                value={territories.provinces?.find(c => c?.code === value) ?? null}
                            />
                        )}>
                    </Controller>
                    <span>
                        {errors.provincia?.type === 'required' &&
                            <p className='text-danger' role="alert">{errors.provincia?.message}</p>}
                    </span>
                </div>

                <div className="custom-form__control col-span-full tab-port:col-span-2">
                    <label className='custom-form__label'>
                        Municipio
                        <span className="text-danger"> *</span>
                    </label>
                    <Controller
                        control={control}
                        name="municipio"
                        rules={{ required: { value: true, message: 'Este campo es requerido' } }}
                        render={({
                            field: { onChange, name, value, ref, onBlur },
                        }) => (
                            <Select
                                ref={ref}
                                onBlur={onBlur}
                                styles={colourStyles}
                                menuPlacement="top"
                                noOptionsMessage={() => "Sin datos"}
                                placeholder="Seleccione un municipio"
                                getOptionLabel={(option) => option?.name}
                                getOptionValue={(option) => option?.code}
                                options={territories.municipalities}
                                isLoading={loadingSet.municipalities}
                                onChange={(newValue) => {
                                    onChange(newValue?.code)
                                    setValue("ciudad", null)
                                }}
                                value={territories.municipalities?.find(c => c?.code === value) ?? null}
                            />
                        )}>
                    </Controller>
                    <span>
                        {errors.municipio?.type === 'required' &&
                            <p className='text-danger' role="alert">{errors.municipio?.message}</p>}
                    </span>
                </div>

                <div className="custom-form__control col-span-full tab-port:col-span-2">
                    <label className='custom-form__label'>
                        Ciudad o Sección
                        <span className="text-danger"> *</span>
                    </label>
                    <Controller
                        control={control}
                        name="ciudad"
                        rules={{ required: { value: true, message: 'Este campo es requerido' } }}
                        render={({
                            field: { onChange, name, value, ref, onBlur },
                        }) => (
                            <Select
                                ref={ref}
                                onBlur={onBlur}
                                styles={colourStyles}
                                menuPlacement="top"
                                noOptionsMessage={() => "Sin datos"}
                                placeholder="Seleccione un ciudad"
                                isLoading={loadingSet.districts}
                                getOptionLabel={(option) => option?.name}
                                getOptionValue={(option) => option?.code}
                                options={territories.districts}
                                onChange={(newValue) => {
                                    onChange(newValue?.code)
                                }}
                                value={territories.districts?.find(c => c?.code === value) ?? null}
                            />
                        )}>
                    </Controller>
                    <span>
                        {errors.ciudad?.type === 'required' &&
                            <p className='text-danger' role="alert">{errors.ciudad?.message}</p>}
                    </span>
                </div>

                <div className="custom-form__control col-span-full tab-port:col-span-2">
                    <label className='custom-form__label' htmlFor="paraje">
                        Paraje
                    </label>
                    <input className='custom-form__input' id="paraje" type="text"
                        {...register('paraje')} />
                </div>

                <button
                    onClick={handleSubmit(submitForm)}
                    className="col-span-full mt-8 mb-4 py-4 px-28 text-2xl bg-[#003670] text-white rounded-lg"
                >
                    Agregar
                </button>
            </form>
        </div>
    )
}

export const GeneratorServiceProviderDatatable = ({ rows = [], onDelete, onEdit }) => {

    const ActionButtons = (params) => (
        <div className="flex gap-x-4 items-center">
            <button
                onClick={() => onEdit(params.row?._id)}
                className="py-4 px-10 text-2xl bg-warning text-white rounded-lg">
                <AiOutlineEdit />
            </button>
            <button
                onClick={() => onDelete(params.row?._id)}
                className="py-4 px-10 text-2xl bg-danger text-white rounded-lg"
            >
                <AiOutlineDelete />
            </button>
        </div>
    )

    /**
     * @type {GridColDef}
     */
    const columns = [
        { field: 'nombre', headerName: 'Nombre', flex: 1, headerClassName: 'text-xl ' },
        { field: 'numeroDocumento', headerName: 'Identificación', flex: 1, headerClassName: 'text-xl ' },
        { field: 'correo', headerName: 'Correo', flex: 1, headerClassName: 'text-xl ' },
        { field: 'telefono', headerName: 'Teléfono', flex: 1, headerClassName: 'text-xl ' },
        { field: 'numeroAutorizacionAmbiental', headerName: 'Autorización Ambiental', flex: 1, headerClassName: 'text-xl ' },
        { field: 'accion', headerName: 'Acciones', flex: 2, headerClassName: 'text-xl ', 
            renderCell: (params) => <ActionButtons {...params} /> }
    ]

    return (
        <Box sx={{ height: "400px", width: '100%' }}>
            <DataGrid
                rows={rows}
                getRowId={(row) => row?._id}
                getRowClassName={(param) => `text-lg `}
                pageSize={10}
                rowsPerPageOptions={[5, 10]}
                columns={columns}
                disableSelectionOnClick
            />
        </Box>
    )
}
